import React, {useEffect, useState} from "react";
import { Card, Button, Spinner, Table } from "react-bootstrap";
import { fetchUserLogsBA } from "../../http/userLogAPI";

const SimpleLogsViewerBA = ({ userId, type, targetId, limit = 500, width }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [openRows, setOpenRows] = useState(new Set());

    useEffect(() => {
        setLogs([]);
        setError("");
    }, [userId, type, targetId]);

    const handleLoadLogs = async () => {
        if (!userId || !type || !targetId) return;
        setLoading(true);
        setError("");
        try {
            const data = await fetchUserLogsBA(userId, type, targetId, limit);
            setLogs(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            setError("Failed to load logs. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const toggleRow = (id) =>
        setOpenRows((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });

    const isOpen = (id) => openRows.has(id);


    const handleClearLogs = () => {
        setLogs([]);
        setError("");
    };

    const formatDate = (ts) => {
        if (!ts) return "—";
        return new Date(ts * 1000).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    };

    return (
        <Card className="p-3 mt-0 border border-secondary"  >
            {/* Заголовок + кнопки (адаптивно) */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">

                {/* Левая часть: Заголовок + подпись */}
                <div className="d-flex flex-column">
                    <h6 className="text-muted mb-1">
                        {type === 'book'
                            ? "Activity Logs for This Book/Product:"
                            : "Activity Logs for This Assignment:"}
                    </h6>


                    {/* Мягкое сервисное сообщение под заголовком */}
                    {!loading && logs.length === 0 && !error && (
                        <span className="small text-muted">
                No logs loaded yet — press <span className="fw-semibold">Get Logs</span>.
            </span>
                    )}
                </div>

                {/* Правая часть: кнопки */}
                <div className="d-flex gap-2 mt-1 mt-md-0">
                    <Button
                        variant="outline-danger"
                        className="btn-sm"
                        onClick={handleClearLogs}
                        disabled={logs.length === 0}
                    >
                        Clear Logs
                    </Button>

                    <Button
                        variant="outline-secondary"
                        className="btn-sm"
                        onClick={handleLoadLogs}
                        disabled={loading || !userId || !type || !targetId}
                    >
                        {loading && (
                            <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                                aria-hidden="true"
                            />
                        )}
                        Get Logs
                    </Button>
                </div>
            </div>

            {error && (
                <p className="text-danger small mt-2 mb-0">
                    {error}
                </p>
            )}

            {/* Состояние загрузки */}
            {loading && (
                <div className="text-center mt-3">
                    <Spinner animation="border" size="sm" />
                </div>
            )}

            {/* Есть логи — мобильные карточки */}
            {!loading && logs.length > 0 && (
                <>
                    {/* 📱 Mobile cards */}
                    <div className="d-md-none mt-3">
                        {logs.map((log) => {
                            const change =
                                Number(log.amount) === 0 || log.amount == null
                                    ? "Unchanged"
                                    : `${log.amount > 0 ? "+" : ""}${Math.round(log.amount)} Virt`;
                            const balance =
                                log.balance_state == null || isNaN(Number(log.balance_state))
                                    ? "N/A"
                                    : `${Math.round(Number(log.balance_state))} Virt`;

                            return (
                                <div
                                    key={log.id}
                                    className={`card shadow-sm mb-2 ${isOpen(log.id) ? "border-success" : ""}`}
                                    role="button"
                                    aria-expanded={isOpen(log.id)}
                                    onClick={() => toggleRow(log.id)}
                                >
                                    <div className="card-body py-2">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted text-nowrap me-2">
                                                {isOpen(log.id) ? "▾ " : "▸ "}Change
                                            </small>
                                            <span
                                                className="small text-end text-truncate fw-semibold"
                                                style={{ maxWidth: "55%" }}
                                            >
                            {change}
                        </span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted text-nowrap me-2">Balance</small>
                                            <span
                                                className="text-end small text-truncate"
                                                style={{ maxWidth: "65%" }}
                                            >
                            {balance}
                        </span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted text-nowrap me-2">Description</small>
                                            <span
                                                className="text-end small text-truncate"
                                                style={{ maxWidth: "65%" }}
                                            >
                            {log.description || "—"}
                        </span>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted text-nowrap me-2">Date</small>
                                            <span
                                                className="text-end small text-truncate"
                                                style={{ maxWidth: "65%" }}
                                            >
                            {formatDate(log.user_log_date)}
                        </span>
                                        </div>

                                        {isOpen(log.id) && (
                                            <div className="mt-2 p-2 rounded-2 border border-1 border-secondary-subtle bg-light">
                                                <div className="text-muted small mb-1">
                                                    Full text of this log:
                                                </div>
                                                <div
                                                    className="text-muted small"
                                                    style={{ whiteSpace: "pre-wrap" }}
                                                >
                                                    {log.description || "—"}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>


                    {/* 💻 Desktop table */}
                    <div className="d-none d-md-block mt-3" style={{ overflowX: "auto", whiteSpace: "nowrap", paddingBottom: "8px" }}>
                        <Table
                            bordered
                            hover
                            size="sm"
                            striped
                            className="mt-1"
                            style={{ minWidth: "400px", maxWidth: "800px", lineHeight: "1.3" }}
                        >
                            <thead className="table-light text-muted small">
                            <tr className="bg-secondary text-center bg-opacity-50 text-sm">
                                <th className="small text-muted">ID</th>
                                <th className="small text-muted">Amount</th>
                                <th className="small text-muted">Balance</th>
                                <th className="small text-muted">Description</th>
                                <th className="small text-muted">Date</th>
                            </tr>
                            </thead>
                            <tbody>
                            {logs.map((log) => {
                                const change =
                                    Number(log.amount) === 0 || log.amount == null
                                        ? "Unchanged"
                                        : `${log.amount > 0 ? "+" : ""}${Math.round(log.amount)} Virt`;
                                const balance =
                                    log.balance_state == null || isNaN(Number(log.balance_state))
                                        ? "N/A"
                                        : `${Math.round(Number(log.balance_state))} Virt`;
                                return (
                                    <React.Fragment key={log.id}>
                                        <tr
                                            onClick={() => toggleRow(log.id)}
                                            style={{ cursor: "pointer" }}
                                            className={
                                                isOpen(log.id)
                                                    ? "table-active small fst-italic"
                                                    : "small fst-italic"
                                            }
                                            role="button"
                                            aria-expanded={isOpen(log.id)}
                                        >
                                            <td
                                                className="small text-center align-middle text-muted"
                                                style={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    maxWidth: "60px",
                                                    width: "60px",
                                                }}
                                            >
                                                {isOpen(log.id) ? "▾ " : "▸ "}
                                                {log.id || "—"}
                                            </td>
                                            <td
                                                className="text-center small text-success fw-bold align-middle"
                                                style={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                              {change || "—"}
                                            </td>
                                            <td
                                                className="text-center small text-success fw-bold align-middle"
                                                style={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {balance || "—"}
                                            </td>
                                            <td
                                                className="small align-middle text-muted"
                                                style={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    maxWidth: width,
                                                    width: width,

                                                }}
                                            >
                                                {log.description || "—"}
                                            </td>
                                            <td
                                                className="text-center small text-muted align-middle"
                                                style={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                {formatDate(log.user_log_date)}
                                            </td>
                                        </tr>

                                        {isOpen(log.id) && (
                                            <tr className="small">
                                                <td colSpan={5} className="text-start py-2">
                                                    <div className="p-2 rounded-2 border border-1 border-secondary-subtle bg-light">
                                                        <div className="text-muted fw-semibold mb-1">
                                                            Full text of this log:
                                                        </div>
                                                        <div
                                                            className="text-muted"
                                                            style={{ whiteSpace: "pre-wrap" }}
                                                        >
                                                            {log.description || "—"}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            </tbody>

                        </Table>
                    </div>
                </>
            )}
        </Card>
    );
};

export default SimpleLogsViewerBA;
